import React, { useCallback, useEffect, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { AppUser, Brand } from '../types';
import { useAuth } from '../auth/AuthContext';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusBanner, { Status } from '../components/StatusBanner';

const Users: React.FC = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<Status>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<AppUser | null>(null);
  const [form, setForm] = useState({
    username: '', full_name: '', role: 'readonly', brand_id: '', is_active: true, password: '',
  });
  const [confirmDelete, setConfirmDelete] = useState<AppUser | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [u, b] = await Promise.all([apiService.getUsers(), apiService.getBrands()]);
      setUsers(u);
      setBrands(b);
    } catch (error) {
      setStatus({ kind: 'error', text: `Could not load users: ${(error as Error).message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ username: '', full_name: '', role: 'readonly', brand_id: '', is_active: true, password: '' });
    setIsModalOpen(true);
  };

  const openEdit = (u: AppUser) => {
    setEditing(u);
    setForm({
      username: u.username,
      full_name: u.full_name || '',
      role: u.role,
      brand_id: u.brand_id === null ? '' : String(u.brand_id),
      is_active: u.is_active,
      // Blank means "leave the password alone" -- the API never returns it.
      password: '',
    });
    setIsModalOpen(true);
  };

  const save = async () => {
    try {
      setSaving(true);
      setStatus(null);
      const brandId = form.brand_id === '' ? null : Number(form.brand_id);

      if (editing) {
        await apiService.updateUser(editing.id, {
          full_name: form.full_name || null,
          role: form.role,
          brand_id: brandId,
          is_active: form.is_active,
          ...(form.password ? { password: form.password } : {}),
        });
        setStatus({
          kind: 'ok',
          text: form.password
            ? `Saved "${editing.username}". Their open sessions were signed out.`
            : `Saved "${editing.username}".`,
        });
      } else {
        await apiService.createUser({
          username: form.username,
          password: form.password,
          full_name: form.full_name || null,
          role: form.role,
          brand_id: brandId,
          is_active: form.is_active,
        });
        setStatus({ kind: 'ok', text: `User "${form.username}" created.` });
      }
      setIsModalOpen(false);
      await load();
    } catch (error) {
      setStatus({ kind: 'error', text: `Could not save the user: ${(error as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u: AppUser) => {
    try {
      setSaving(true);
      setStatus(null);
      await apiService.deleteUser(u.id);
      setConfirmDelete(null);
      setStatus({ kind: 'ok', text: `User "${u.username}" deleted.` });
      await load();
    } catch (error) {
      setStatus({ kind: 'error', text: `Could not delete the user: ${(error as Error).message}` });
    } finally {
      setSaving(false);
    }
  };

  const canSave = editing
    ? true
    : form.username.length >= 3 && form.password.length >= 8;

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Users</h1>
          <p className="mt-2 text-sm text-gray-700">
            Administrators may change things; readonly accounts may only look. An account tied to a
            brand sees only that brand — its networks, its logs and its statistics.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            Add user
          </button>
        </div>
      </div>

      <StatusBanner status={status} />

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Username</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Name</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Role</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Brand</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Last sign-in</th>
                    <th className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {loading ? (
                    <tr><td colSpan={7} className="text-center py-4">Loading...</td></tr>
                  ) : users.map((u) => (
                    <tr key={u.id}>
                      <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                        {u.username}
                        {me?.id === u.id && <span className="ml-2 text-xs text-gray-500">(you)</span>}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500">{u.full_name || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          u.role === 'admin' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {u.role === 'admin' ? 'Administrator' : 'Read only'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {u.brand_name || <span className="text-gray-400">All brands</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {u.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 space-x-3">
                        <button onClick={() => openEdit(u)} className="text-indigo-600 hover:text-indigo-900">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(u)}
                          disabled={me?.id === u.id}
                          title={me?.id === u.id ? 'You cannot delete your own account' : undefined}
                          className="text-red-600 hover:text-red-900 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editing ? `Edit ${editing.username}` : 'Add user'}
        footer={
          <>
            <button
              type="button"
              onClick={save}
              disabled={saving || !canSave}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : editing ? 'Save' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
            >
              Cancel
            </button>
          </>
        }
      >
        {!editing && (
          <div>
            <label htmlFor="u-username" className="block text-sm font-medium text-gray-700">
              Username <span className="text-red-500">*</span>
            </label>
            <input
              id="u-username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">Stored lowercase; at least 3 characters.</p>
          </div>
        )}
        <div>
          <label htmlFor="u-name" className="block text-sm font-medium text-gray-700">Full name</label>
          <input
            id="u-name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
        <div>
          <label htmlFor="u-password" className="block text-sm font-medium text-gray-700">
            Password {!editing && <span className="text-red-500">*</span>}
          </label>
          <input
            id="u-password"
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            {editing
              ? 'Leave blank to keep the current password. Setting one signs the account out everywhere.'
              : 'At least 8 characters.'}
          </p>
        </div>
        <div>
          <label htmlFor="u-role" className="block text-sm font-medium text-gray-700">Role</label>
          <select
            id="u-role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="readonly">Read only — can look, cannot change</option>
            <option value="admin">Administrator — can change</option>
          </select>
        </div>
        <div>
          <label htmlFor="u-brand" className="block text-sm font-medium text-gray-700">Brand</label>
          <select
            id="u-brand"
            value={form.brand_id}
            onChange={(e) => setForm({ ...form, brand_id: e.target.value })}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="">All brands</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Restricting to a brand hides every other brand, and filters logs and statistics to it.
            Only an account set to all brands can manage users.
          </p>
        </div>
        <div className="flex items-center">
          <input
            id="u-active"
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="u-active" className="ml-2 block text-sm text-gray-900">Active</label>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete user"
        destructive
        busy={saving}
        confirmLabel="Delete"
        message={<>Delete <strong>{confirmDelete?.username}</strong>? They will be signed out immediately.</>}
        onConfirm={() => confirmDelete && remove(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default Users;
